import React, { useEffect, useState } from "react";
import { ADMIN_API_URL } from "../../constants";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import CustomerFormModal from "../../components/customers/CustomerFormModal";
import { useNavigate } from "react-router-dom";

const CustomerManagement = ({ addNotification }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10); // Default records per page
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [editCustomer, setEditCustomer] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            addNotification("Authorization token not found. Please log in again.", 'error');
            navigate("/");
            return;
        }
        fetchCustomers();
    }, [currentPage, recordsPerPage, searchTerm, navigate]);

    const fetchCustomers = async () => {
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            addNotification("Authorization token not found. Please log in again.", 'error');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}customers?page=${currentPage}&limit=${recordsPerPage}${searchTerm ? `&search=${searchTerm}` : ''}`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`
                }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch customers.");
            }

            const result = await response.json();
            console.log("Customers API response:", result);
            console.log("Total pages from backend:", result.pagination.totalPages);
            setCustomers(result.data || []); // Use data directly from backend
            setTotalRecords(result.pagination.totalRecords || 0); // Use totalRecords from backend
            setTotalPages(result.pagination.totalPages || 0); // Use totalPages from backend
            setCurrentPage(result.pagination.currentPage || 1); // Set current page from backend

        } catch (err) {
            setError(err.message);
            addNotification(`Error fetching customers: ${err.message}`, 'error');
            setCustomers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleAddCustomer = () => {
        setEditCustomer(null);
        setShowModal(true);
    };

    const handleEditCustomer = async (customer) => {
        // Fetch full customer details before opening modal, in case table data is partial
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            addNotification("Authorization token not found. Please log in again.", 'error');
            setLoading(false);
            return;
        }
        try {
            const response = await fetch(`${ADMIN_API_URL}customers/detail/${customer.customerId}`, {
                method: "GET",
                headers: {
                    "Authorization": `${token}`
                }
            });
            if (!response.ok) {
                throw new Error("Failed to fetch customer details.");
            }
            const result = await response.json();
            setEditCustomer(result.data);
            setShowModal(true);
        } catch (err) {
            setError(err.message);
            addNotification(`Error fetching customer details: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCustomer = async (customerId) => {
        if (!window.confirm("Are you sure you want to delete this customer?")) return;

        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            addNotification("Authorization token not found. Please log in again.", 'error');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${ADMIN_API_URL}customers/${customerId}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete customer.");
            }

            addNotification("Customer deleted successfully.", 'success');
            fetchCustomers(); // Refresh customers list
        } catch (err) {
            setError(err.message);
            addNotification(`Error deleting customer: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = (customerId) => {
        navigate(`/customers/${customerId}`);
    };

    const handleModalClose = () => {
        setShowModal(false);
        setEditCustomer(null);
    };

    const handleModalSave = async (customer) => {
        setLoading(true);
        setError(null);
        const token = sessionStorage.getItem("authToken");
        if (!token) {
            setError("Authorization token not found.");
            addNotification("Authorization token not found. Please log in again.", 'error');
            setLoading(false);
            return;
        }

        try {
            let response;
            if (customer.id) {
                // Update existing customer
                // Assuming PATCH /admin/v1.0/customers/:id for update based on other APIs
                response = await fetch(`${ADMIN_API_URL}customers/${customer.customerId}`, {
                    method: "PATCH",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                    body: JSON.stringify(customer),
                });
            } else {
                // Add new customer
                response = await fetch(`${ADMIN_API_URL}create-customer`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `${token}`,
                    },
                    body: JSON.stringify(customer),
                });
            }

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || `Failed to ${customer.id ? 'update' : 'create'} customer.`);
            }

            addNotification(`Customer ${customer.id ? 'updated' : 'created'} successfully.`, 'success');
            setShowModal(false);
            setEditCustomer(null);
            fetchCustomers(); // Refresh customers list
        } catch (err) {
            setError(err.message);
            addNotification(`Error ${customer.id ? 'updating' : 'creating'} customer: ${err.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleRecordsPerPageChange = (e) => {
        setRecordsPerPage(Number(e.target.value));
        setCurrentPage(1); // Reset to first page
    };

    // Helper to format date for display
    const formatDateForDisplay = (isoDateString) => {
        if (!isoDateString) return 'N/A';
        const date = new Date(isoDateString);
        return date.toLocaleDateString(); // e.g., 6/13/2025
    };

    return (
        <>
            <Header />
            <div id="wrapper" style={{ paddingTop: '70px' }}>
                <div className="normalheader">
                    <div className="hpanel">
                        <div className="panel-body">
                            <h2 className="font-light m-b-xs">Customer Management</h2>
                        </div>
                    </div>
                </div>

                {/* Filter and Add Customer Section */}
                <div className="content">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    <div className="row mb-3">
                                        <div className="col-md-6">
                                                <input
                                                    type="text"
                                                placeholder="Search by name or email"
                                                    className="form-control"
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customers Table */}
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="hpanel">
                                <div className="panel-body">
                                    {loading && <p>Loading customers...</p>}
                                    {error && <p className="text-danger">Error: {error}</p>}
                                    <div className="table-responsive">
                                        <table cellPadding="1" cellSpacing="1" className="table">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Phone</th>
                                                    <th>Customer ID</th>
                                                    <th>Created At</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {customers.length > 0 ? (
                                                    customers.map((customer) => (
                                                        <tr key={customer.id}>
                                                            <td>{customer.name || "N/A"}</td>
                                                            <td>{customer.email || "N/A"}</td>
                                                            <td>{customer.mobile || "N/A"}</td>
                                                            <td>{customer.customerId || "N/A"}</td>
                                                            <td>{formatDateForDisplay(customer.createdAt)}</td>
                                                            <td>
                                                                <div className="dropdown">
                                                                    <button
                                                                        className="btn btn-sm dropdown-toggle"
                                                                        type="button"
                                                                        id={`dropdownMenuButton-${customer.id}`}
                                                                        data-toggle="dropdown"
                                                                        aria-haspopup="true"
                                                                        aria-expanded="false"
                                                                        style={{ backgroundColor: '#34495e', color: '#fff' }}
                                                                    >
                                                                        Actions <span className="caret"></span>
                                                                    </button>
                                                                    <ul className="dropdown-menu" aria-labelledby={`dropdownMenuButton-${customer.id}`}>
                                                                        <li>
                                                                            <a
                                                                                className="dropdown-item"
                                                                                href="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleViewDetails(customer.customerId);
                                                                                }}
                                                                            >
                                                                                View Details
                                                                            </a>
                                                                        </li>
                                                                        <li>
                                                                            <a
                                                                                className="dropdown-item"
                                                                                href="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleEditCustomer(customer);
                                                                                }}
                                                                            >
                                                                                Edit
                                                                            </a>
                                                                        </li>
                                                                        <li>
                                                                            <a
                                                                                className="dropdown-item text-danger"
                                                                                href="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    handleDeleteCustomer(customer.customerId);
                                                                                }}
                                                                            >
                                                                                Delete
                                                                            </a>
                                                                        </li>
                                                                    </ul>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="6" className="text-center">No customers found</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Pagination Controls */}
                                    <div className="text-right m-t-md">
                                        <label htmlFor="recordsPerPage" className="m-r-sm">Records per page:</label>
                                        <select
                                            id="recordsPerPage"
                                            className="form-control input-sm"
                                            style={{ display: 'inline-block', width: 'unset' }}
                                            value={recordsPerPage}
                                            onChange={handleRecordsPerPageChange}
                                        >
                                            <option value={10}>10</option>
                                            <option value={20}>20</option>
                                            <option value={50}>50</option>
                                            <option value={100}>100</option>
                                        </select>
                                        <button
                                            className="btn btn-default m-r-sm m-l-sm"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </button>
                                        <span>Page {currentPage} of {totalPages}</span>
                                        <button
                                            className="btn btn-default m-l-sm"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>

            {showModal && (
                <CustomerFormModal
                    show={showModal}
                    onClose={handleModalClose}
                    onSave={handleModalSave}
                    customer={editCustomer}
                />
            )}
        </>
    );
};

export default CustomerManagement;